/* eslint-disable @typescript-eslint/no-explicit-any */
import isEmpty from 'lodash/isEmpty';
import { useEffect, useState } from 'react';
import type { IRequestState } from '@/Interfaces/Dto';

import Requests from '@/Services/Requests';
import { objectNotNull } from '@/Utils/FnUtils';
import { useAppDispatch, useAppSelector } from '@/_store/hooks';
import {
  motoboyInfoRequest,
  selectMotoboyInfo,
} from '@/_store/modules/motoboy-info';

const useMotoboy = (motoboyIdProps: string) => {
  const dispatch = useAppDispatch();
  const loadingMotoboy = useAppSelector(state => state.motoboyInfo.fetching);
  const errors = useAppSelector(state => state.motoboyInfo.errors);
  const motoboy = useAppSelector(selectMotoboyInfo);

  const [createMotoboyState, setCreateMotoboyState] = useState<IRequestState>({
    loading: false,
    errors: [],
  });

  const [editMotoboyState, setEditMotoboyState] = useState<IRequestState>({
    loading: false,
    errors: [],
  });

  const [motoboyId, setMotoboyId] = useState(motoboyIdProps);

  const isMotoboyBlocked = motoboy?.blocks && motoboy.blocks.length > 0;

  useEffect(() => {
    if (motoboy?._id !== motoboyId && !loadingMotoboy && !isEmpty(motoboyId)) {
      updateMotoboy(motoboyId);
    }
  }, [motoboy, motoboyId, loadingMotoboy]);

  function updateMotoboy(newMotoboyId: string) {
    if (motoboyId !== newMotoboyId) {
      setMotoboyId(motoboyId);
    }

    dispatch(motoboyInfoRequest(newMotoboyId));
  }

  async function handleCreateMotoboy(payload: any) {
    setCreateMotoboyState({ loading: true, errors: [] });

    let created = false;

    try {
      const response = await Requests.motoboys.createMotoboy(
        objectNotNull(payload),
      );

      if (!response.r) {
        setCreateMotoboyState({ loading: false, errors: response.errors });

        return;
      }

      setCreateMotoboyState({ loading: false, errors: [] });
      created = true;
    } catch (err: any) {
      const errorMessage = err.response?.data.errors;

      setCreateMotoboyState({ loading: false, errors: [errorMessage] });
    }

    return created;
  }

  async function handleEditMotoboy(payload: any) {
    setEditMotoboyState({ loading: true, errors: [] });

    let edited = false;

    try {
      if (!motoboy || isEmpty(motoboy)) {
        throw new Error('Falha ao atualizar! Entregador inexistente');
      }

      const response = await Requests.motoboys.editMotoboy(
        motoboy._id,
        payload,
      );

      if (!response.r) {
        setEditMotoboyState({ loading: false, errors: response.errors });

        return false;
      }

      updateMotoboy(motoboy._id);
      setEditMotoboyState({ loading: false, errors: [] });
      edited = true;
    } catch (err: any) {
      const errorMessage = err.response?.data.error;
      setEditMotoboyState({ loading: false, errors: [errorMessage] });
    }

    return edited;
  }

  return {
    motoboy,
    loadingMotoboy,
    errorsMotoboy: errors,
    updateMotoboy,
    isMotoboyBlocked,
    createMotoboyState,
    editMotoboyState,
    handleEditMotoboy,
    handleCreateMotoboy,
  };
};

export default useMotoboy;
